// Strata router — react-router-dom 6.
//
// Routes:
//   /login                  — public OTP login
//   /                       — Dashboard (the Strata shell wraps every authed route)
//   /merchants              — admin only (list)
//   /merchants/:id          — single merchant (admin or member)
//   /merchants/:id/ledger   — full ledger management for the merchant
//   /ledger                 — same page for the switcher-selected merchant
//   /fees                   — per-merchant fee scales (superadmin only)
//   /payouts                — payouts table
//   /banks                  — bank-rails screen ("my accounts" for merchants)
//   /keys                   — API keys
//   /rbac                   — users & roles
//   *                       — 404
import { createBrowserRouter } from "react-router-dom";

import { App } from "@/App";
import { RequireAuth } from "@/guards/RequireAuth";
import { Login } from "@/pages/Login";
import { Dashboard } from "@/pages/Dashboard";
import { Merchants } from "@/pages/Merchants";
import { MerchantDetail } from "@/pages/MerchantDetail";
import { MerchantLedger } from "@/pages/MerchantLedger";
import { BatchDetail } from "@/pages/BatchDetail";
import { PayoutDetail } from "@/pages/PayoutDetail";
import { Payouts } from "@/pages/Payouts";
import { Approvals } from "@/pages/Approvals";
import { Beneficiaries } from "@/pages/Beneficiaries";
import { BeneficiaryDetail } from "@/pages/BeneficiaryDetail";
import { Customers } from "@/pages/Customers";
import { CustomerDetail } from "@/pages/CustomerDetail";
import { BankAccounts } from "@/pages/BankAccounts";
import { ApiKeys } from "@/pages/ApiKeys";
import { ApiDocs } from "@/pages/ApiDocs";
import { Webhooks } from "@/pages/Webhooks";
import { Rbac } from "@/pages/Rbac";
import { PlatformRails } from "@/pages/PlatformRails";
import { PlatformPayouts } from "@/pages/PlatformPayouts";
import { ProviderLogs } from "@/pages/ProviderLogs";
import { AdminRails } from "@/pages/AdminRails";
import { AdminAuditLog } from "@/pages/AdminAuditLog";
import { ProviderStatus } from "@/pages/ProviderStatus";
import { Reports } from "@/pages/Reports";
import { Fees } from "@/pages/Fees";
import ManualPayouts from "@/pages/ManualPayouts";
import { Settings } from "@/pages/Settings";
import { NotFound } from "@/pages/NotFound";

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <Login />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <App />,
        children: [
          { index: true, element: <Dashboard /> },
          {
            element: <RequireAuth role="admin" />,
            children: [
              { path: "merchants", element: <Merchants /> },
              // Migration 019 — admin approval queue. Admin-only because
              // the underlying GET /v1/admin/payouts/needs-approval is
              // gated by RequireRole(admin) on the backend; gating the
              // route here gives a clean 'unauthorized' redirect instead
              // of a 403 toast on first nav.
              { path: "approvals", element: <Approvals /> },
              // Per-payout drill-down. Admin-only because the backing
              // endpoint (GET /v1/admin/payouts/:id) surfaces the raw
              // provider request/response payload via the audit_log
              // jsonb columns — merchants must never see upstream PSP
              // payloads, only the (status, error_code, error_message)
              // slice on the list endpoint.
              { path: "payouts/:payoutId", element: <PayoutDetail /> },
              // Phase 8 — admin-only platform tier.
              { path: "platform/rails", element: <PlatformRails /> },
              { path: "platform/payouts", element: <PlatformPayouts /> },
              // Admin diagnostics — outbound PSP call log (migration 023).
              // Reveals every upstream rail request/response for triage.
              { path: "admin/provider-logs", element: <ProviderLogs /> },
              // PDF round 3 N5 — cross-tenant rails view. Admin-only
              // because the response decrypts every IDFC credential
              // envelope to render the obfuscated triple — even though
              // the wire never carries plaintext, the listing itself is
              // operator-only by design.
              { path: "admin/rails", element: <AdminRails /> },
              // Admin-only audit log viewer. Cross-tenant — every
              // audit_log row across the platform with filters for the
              // common forensic queries. Backed by /v1/admin/audit-log.
              { path: "admin/audit-log", element: <AdminAuditLog /> },
              // Admin-only Razorpay payment-downtimes board. Backed by
              // GET /v1/admin/razorpay/downtimes which proxies to
              // api.razorpay.com server-side so credentials never reach
              // the browser. Polls every 60s.
              { path: "admin/provider-status", element: <ProviderStatus /> },
            ],
          },
          { path: "merchants/:id", element: <MerchantDetail /> },
          { path: "merchants/:id/ledger", element: <MerchantLedger /> },
          // Sidebar ledger. Same page, no :id — the merchant comes from
          // the header switcher (ui.currentMerchantId), matching how every
          // other id-less screen scopes itself via X-Merchant-ID.
          { path: "ledger", element: <MerchantLedger /> },
          { path: "payouts", element: <Payouts /> },
          { path: "payouts/batches/:batchId", element: <BatchDetail /> },
          // Reachable from both admin and merchant sidebars. The merchant
          // sidebar had no path to manage beneficiaries at all before this.
          { path: "beneficiaries", element: <Beneficiaries /> },
          {
            path: "beneficiaries/:beneficiaryId",
            element: <BeneficiaryDetail />,
          },
          // PDF round 2 item 5 — customer directory (migration 025).
          // One customer = (email, phone) on a merchant; 1..N
          // beneficiary rows (bank accounts) per customer.
          { path: "customers", element: <Customers /> },
          { path: "customers/:customerId", element: <CustomerDetail /> },
          { path: "banks", element: <BankAccounts /> },
          // Reports — three streamed downloads (statement / audit /
          // reconciliation). Accessible to both admin and merchant
          // roles; the backend pins tenant scope via membership
          // (RequireMembershipRole viewer+) so a merchant only sees
          // their own data, an admin sees the X-Merchant-ID-selected
          // merchant's data.
          { path: "reports", element: <Reports /> },
          // Per-merchant fee scales. SUPERADMIN only — the page renders its
          // own "ask a superadmin" state for everyone else rather than
          // 404ing, because an admin who follows a link here should be told
          // why they can't, not that the page doesn't exist. The backend
          // (auth.RequireSuperadmin) is the actual gate.
          { path: "fees", element: <Fees /> },
          // Manual rail — CSV payment files + the layouts they use. One route
          // for both halves of the job; the page tabs between them so the
          // sidebar gains one entry, not two.
          { path: "manual-payouts", element: <ManualPayouts /> },
          { path: "keys", element: <ApiKeys /> },
          { path: "keys/docs", element: <ApiDocs /> },
          { path: "keys/webhooks", element: <Webhooks /> },
          { path: "rbac", element: <Rbac /> },
          // Signed-in user settings — Security section (passkey enrolment +
          // management). Available to every authed role; the underlying
          // /v1/auth/webauthn/* endpoints are self-scoped by the caller's JWT.
          { path: "settings", element: <Settings /> },
          // AUDIT-PASS-5 UI #23: 404 inside the authed App shell. An
          // authenticated operator who lands on a bad URL now sees the
          // Strata sidebar + topbar with the not-found copy in the
          // content area, instead of a blank standalone page that
          // looks like they got logged out.
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
  // Unauthed users hitting an unknown URL still get the standalone
  // 404 (they can't render the shell). RequireAuth redirects them
  // through /login first if they're hitting a protected path.
  { path: "*", element: <NotFound /> },
]);
